import React, { useEffect } from "react"
import Paper from "../../ui/Paper"
import { useDispatch, useSelector } from "react-redux"
import { addUserRSO, selectCreatingUserRSO, selectRSOTypes } from "./slice"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import styled from "styled-components"
import Label from "../../ui/Label"
import FormError from "../../ui/FormError"
import TextArea from "../../ui/TextArea"
import FormGroup from "../../ui/FormGroup"
import Button from "../../ui/Button"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import ErrorMsg from "../../ui/ErrorMsg"
import PropTypes from "prop-types"
import { slideFromRight } from "../../ui/animations"
import Tip from "../../ui/Tip"
import { validateTLE } from "./tle"
import { useNavigate } from "react-router-dom"
import Select from "../../ui/Select"
import Input from "../../ui/Input"

const Panel = styled(Paper)`
  padding: 2rem;
  overflow-y: auto;
  width: 50vw;
  min-width: 20rem;
  max-width: 40rem;
  animation: 0.3s ${slideFromRight} ease-out;
`
const FormContainer = styled.div``

/**
 * The form validator
 */
const schema = yup
  .object({
    tle: yup
      .string()
      .test("is-valid-tle", "TLE is not well-formed", (value) =>
        validateTLE(value)
      )
      .required("TLE is required"),
    object_id: yup.string(),
    object_type: yup.string(),
    name: yup
      .string()
      .max(22)
      .required("name is required and must be less than 24 chars"),
  })
  .required()

/**
 * Create a new RSO.  User RSOs are treated differently to RSO's generated from external sources such as Spacktrack.
 *
 * @param handleCancel
 * @returns {JSX.Element}
 * @constructor
 */
const CreateUserRSO = ({ handleCancel }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const inProgressList = useSelector((state) => selectCreatingUserRSO(state))
  const rosTypes = useSelector(selectRSOTypes) // The various types of RSO

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      tle: "",
    },
    resolver: yupResolver(schema),
  })
  const onSubmit = (data) => {
    dispatch(addUserRSO(data))
      .unwrap()
      .then((a) => {
        navigate(`/rsos/${a.id}`)
      })
  }

  const tleValue = watch("tle")
  // const nameValue = watch("name")

  // Use useEffect to update the second field when the first field changes
  useEffect(() => {
    if (tleValue?.length > 0) {
      // Split the input string by newline characters to get an array of lines
      const lines = tleValue.split("\n")
      if (lines.length >= 3) {
        let name = lines[0]
        if (lines[0] && lines[0].startsWith("0")) {
          name = lines[0].substring(1)
        }
        // Update the second field based on the value of the first field
        setValue("name", name)
      }
    }
  }, [tleValue])

  // Use useEffect to update the second field when the first field changes
  // useEffect(() => {
  //   if (nameValue?.length > 0) {
  //     try {
  //       setValue("tle", updateTLEObjectName(nameValue, tleValue))
  //     } catch (e) {
  //       console.error(`could not update tle - ${e.message}`)
  //     }
  //   }
  // }, [nameValue])

  return (
    <Panel>
      <h2>New RSO</h2>
      <p>
        Store a new RSO based on a custom TLE that you provide.
        <br />
        This RSO is visible to other users in your organisation and can be added
        to priority lists for observation scheduling.
      </p>
      {inProgressList?._saveStatus === "creating" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      {inProgressList?._saveStatus == "rejected" && (
        <ErrorMsg
          msg={
            inProgressList._errorMessage || "an error occurred creating the RSO"
          }
          showIcon={true}
        />
      )}
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="tle">TLE:</Label>
            <TextArea {...register("tle")} rows="3" />
            <Tip>TLE (each line must be on a new line)</Tip>
            <FormError>{errors.tle?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="label">Display name:</Label>
            <Input type="text" {...register("name")} autoFocus />
            <FormError>{errors.name?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="object_id">COSPAR ID:</Label>
            <Input type="text" {...register("object_id")} autoFocus />
            <Tip>Optional</Tip>
            <FormError>{errors.object_id?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="object_type">Type:</Label>
            <Select {...register("object_type")} autoFocus>
              {Object.values(rosTypes).map((r, i) => (
                <option key={i} value={r}>
                  {r}
                </option>
              ))}
            </Select>
            <Tip>Optional</Tip>
          </FormGroup>
          <Button type="submit" active>
            Submit
          </Button>
          {handleCancel && <Button onClick={handleCancel}>Cancel</Button>}
        </form>
      </FormContainer>
    </Panel>
  )
}

/**
 *  <DropDownFilter
 *                 values={rosTypes.map((rso) => {
 *                   return { name: rso, value: rso }
 *                 })}
 *                 filterByName="object_type"
 *                 filter={filter}
 *                 setFilter={setFilter}
 *                 active={filter.object_type ? true : false}
 *               />
 * @type {{handleCancel: Requireable<(...args: any[]) => any>}}
 */
CreateUserRSO.propTypes = {
  handleCancel: PropTypes.func, // Cancel the creation of an RSO
}
export default CreateUserRSO
